import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { api, ApiRequestError, SESSION_ENDED_EVENT } from '../lib/api';
import type { Permission } from '@meshsmith/shared';

export interface AuthUser {
  id: string;
  /** null for customers who sign in with a phone number and have not added an email */
  email: string | null;
  /** the verified WhatsApp number for customers */
  phone?: string | null;
  firstName: string;
  lastName: string;
  kind: 'CUSTOMER' | 'STAFF';
  emailVerified: boolean;
}

interface MeResponse {
  user: AuthUser | null;
  roles?: string[];
  permissions?: Permission[];
  isSuperAdmin?: boolean;
}

interface AuthCtx {
  user: AuthUser | null;
  roles: string[];
  permissions: Set<Permission>;
  loading: boolean;
  isStaff: boolean;
  isSuperAdmin: boolean;
  can: (permission: Permission) => boolean;
  login: (email: string, password: string) => Promise<AuthUser>;
  /** Sends a 6-digit code to the customer's WhatsApp. */
  requestOtp: (phone: string) => Promise<{ expiresInSeconds: number; resendInSeconds: number }>;
  /** Checks the code and signs the customer in (creating the account the first time). */
  verifyOtp: (phone: string, otp: string) => Promise<{ user: AuthUser; isNew: boolean }>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [roles, setRoles] = useState<string[]>([]);
  const [permissions, setPermissions] = useState<Set<Permission>>(new Set());
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const me = await api.get<MeResponse>('/auth/me');
      setUser(me.user);
      setRoles(me.roles ?? []);
      setPermissions(new Set(me.permissions ?? []));
      setIsSuperAdmin(me.isSuperAdmin ?? false);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // The server renews a session by itself while the refresh token is good. If it says the session is over (signed out
  // elsewhere, the account suspended, a token reused), stop showing this person as signed in.
  useEffect(() => {
    const ended = () => {
      setUser(null);
      setRoles([]);
      setPermissions(new Set());
      setIsSuperAdmin(false);
    };
    window.addEventListener(SESSION_ENDED_EVENT, ended);
    return () => window.removeEventListener(SESSION_ENDED_EVENT, ended);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const res = await api.post<{ user: AuthUser; roles?: string[]; permissions?: Permission[]; isSuperAdmin?: boolean }>('/auth/login', {
      email,
      password,
    });
    setUser(res.user);
    setRoles(res.roles ?? []);
    setPermissions(new Set(res.permissions ?? []));
    setIsSuperAdmin(res.isSuperAdmin ?? false);
    return res.user;
  }, []);

  const requestOtp = useCallback((phone: string) => api.post<{ expiresInSeconds: number; resendInSeconds: number }>('/auth/otp/request', { phone }), []);

  const verifyOtp = useCallback(async (phone: string, otp: string) => {
    const res = await api.post<{ user: AuthUser; isNew: boolean }>('/auth/otp/verify', { phone, otp });
    setUser(res.user);
    setRoles([]);
    setPermissions(new Set());
    setIsSuperAdmin(false);
    return res;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch (e) {
      if (!(e instanceof ApiRequestError)) throw e;
    }
    setUser(null);
    setRoles([]);
    setPermissions(new Set());
    setIsSuperAdmin(false);
  }, []);

  return (
    <Ctx.Provider
      value={{
        user,
        roles,
        permissions,
        loading,
        isStaff: user?.kind === 'STAFF' && roles.length > 0,
        isSuperAdmin,
        can: (p) => permissions.has(p),
        login,
        requestOtp,
        verifyOtp,
        logout,
        refresh: load,
      }}
    >
      {children}
    </Ctx.Provider>
  );
}

export function useAuth(): AuthCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
